import { get } from '../../../utils/Request';

export type ManagedOrder = {
  orderNo?: string;
  userCode?: string;
  userName?: string;
  phone?: string;
  email?: string;
  regionCode?: string;
  orderType?: string;
  productName?: string;
  durationDays?: number | null;
  validityDays?: number | null;
  quantity?: number;
  amountMinor?: number;
  currency?: string;
  currencyFractionDigits?: number;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  paymentChannel?: string;
  createdAt?: string;
  paidAt?: string;
  orderStatus?: string;
  closedAt?: string;
};

export type ManagedOrderPage = {
  records?: ManagedOrder[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

export type ConsoleOrderQuery = {
  pageNum: number;
  pageSize: number;
  orderNo?: string;
  userCode?: string;
  userName?: string;
  phone?: string;
  email?: string;
  orderType?: string;
  orderStatus?: string;
  productName?: string;
  planCode?: string;
  packageCode?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  paymentChannel?: string;
  regionCode?: string;
  currency?: string;
};

export type ManagedOrderFeatureDescription = {
  title?: string;
  displayType?: string;
  supported?: boolean;
  quota?: number;
  unit?: string;
  period?: string;
  text?: string;
};

export type ManagedPayment = {
  channel?: string;
  scene?: string;
  status?: string;
  merchantTradeNo?: string;
  providerTransactionId?: string;
  amountMinor?: number;
  currency?: string;
  currencyFractionDigits?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type ManagedFulfillment = {
  status?: string;
  attemptCount?: number;
  nextRunAt?: string;
};

export type ManagedGiftOperator = {
  adminCode?: string;
  adminName?: string;
  reason?: string;
};

export type ManagedOrderDetail = {
  order?: ManagedOrder;
  featureDescriptions?: ManagedOrderFeatureDescription[];
  unitAmountMinor?: number;
  quoteExpiresAt?: string;
  updatedAt?: string;
  payments?: ManagedPayment[];
  fulfillment?: ManagedFulfillment | null;
  gift?: ManagedGiftOperator | null;
};

/** 分页查询订单。筛选条件走查询参数，空值不传。content 为分页对象。返回体原样交给页面。 */
export function queryConsoleOrders(query: ConsoleOrderQuery) {
  return get<ManagedOrderPage>({
    path: '/api/v1/console/orders',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize,
      orderNo: query.orderNo,
      userCode: query.userCode,
      userName: query.userName,
      phone: query.phone,
      email: query.email,
      orderType: query.orderType,
      orderStatus: query.orderStatus,
      productName: query.productName,
      planCode: query.planCode,
      packageCode: query.packageCode,
      paymentStatus: query.paymentStatus,
      fulfillmentStatus: query.fulfillmentStatus,
      paymentChannel: query.paymentChannel,
      regionCode: query.regionCode,
      currency: query.currency
    }
  });
}

/** 查询订单详情。content.order 为订单，其余为功能说明、支付记录、发放进度和赠送信息。 */
export function getConsoleOrderDetail(orderNo: string) {
  return get<ManagedOrderDetail>({
    path: `/api/v1/console/orders/${encodeURIComponent(orderNo)}`
  });
}

export const MANAGED_ORDER_TYPE_LABEL: Record<string, string> = {
  PLAN: '套餐购买',
  ENTITLEMENT: '独立权益包',
  GIFT: '套餐赠送'
};

export const MANAGED_ORDER_STATUS_LABEL: Record<string, string> = {
  PENDING: '待支付',
  COMPLETED: '已完成',
  CLOSED: '已关闭'
};

export const MANAGED_PAYMENT_STATUS_LABEL: Record<string, string> = {
  UNPAID: '待支付',
  PAID: '已支付',
  NOT_REQUIRED: '无需支付'
};

export const MANAGED_FULFILLMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: '待发放',
  FULFILLED: '已发放',
  MANUAL_REVIEW: '待人工核对'
};

export const MANAGED_PAYMENT_RECORD_STATUS_LABEL: Record<string, string> = {
  INITIATED: '已发起',
  PENDING: '待支付',
  UNKNOWN: '未知',
  SUCCEEDED: '成功',
  FAILED: '失败',
  CLOSED: '已关闭'
};

export const MANAGED_PAYMENT_CHANNEL_LABEL: Record<string, string> = {
  ALIPAY: '支付宝',
  WECHAT: '微信',
  PAYSSION: 'Payssion',
  VIRTUAL: '虚拟支付'
};

export const MANAGED_ORDER_TYPE_OPTIONS = [
  { value: 'PLAN', label: '套餐购买' },
  { value: 'ENTITLEMENT', label: '独立权益包' },
  { value: 'GIFT', label: '套餐赠送' }
];

export const MANAGED_ORDER_STATUS_OPTIONS = [
  { value: 'PENDING', label: '待支付' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'CLOSED', label: '已关闭' }
];

export const MANAGED_PAYMENT_STATUS_OPTIONS = [
  { value: 'UNPAID', label: '待支付' },
  { value: 'PAID', label: '已支付' },
  { value: 'NOT_REQUIRED', label: '无需支付' }
];

export const MANAGED_FULFILLMENT_STATUS_OPTIONS = [
  { value: 'PENDING', label: '待发放' },
  { value: 'FULFILLED', label: '已发放' },
  { value: 'MANUAL_REVIEW', label: '待人工核对' }
];

export const MANAGED_PAYMENT_CHANNEL_OPTIONS = [
  { value: 'ALIPAY', label: '支付宝' },
  { value: 'WECHAT', label: '微信' },
  { value: 'PAYSSION', label: 'Payssion' },
  { value: 'VIRTUAL', label: '虚拟支付' }
];

export function formatMinorAmount(amountMinor?: number, fractionDigits?: number) {
  if (amountMinor == null || !Number.isFinite(amountMinor)) return '—';
  const minor = Math.trunc(amountMinor);
  if (!Number.isSafeInteger(minor)) return String(amountMinor);
  const digits = fractionDigits == null || fractionDigits < 0 ? 0 : Math.min(Math.trunc(fractionDigits), 8);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  if (digits === 0) return sign + abs.toLocaleString('en-US');
  const factor = 10 ** digits;
  const whole = Math.trunc(abs / factor);
  const fraction = String(abs % factor).padStart(digits, '0');
  return `${sign}${whole.toLocaleString('en-US')}.${fraction}`;
}

export type OrderStatus = 'placed' | 'completed' | 'cancelled';
export type PayStatus = 'pending' | 'paid';
export type ArrivalStatus = 'pending' | 'received';
export type OrderType = 'purchase' | 'upgrade' | 'addon';
export type PlaceType = 'page' | 'admin';
export type PayMethod = 'wechat' | 'alipay' | 'other';

export type AdminOrder = {
  id: string;
  orderNo: string;
  userName: string;
  userId: string;
  phone: string;
  email: string;
  country: string;
  packageName: string;
  packagePrice: number;
  orderAmount: number;
  orderStatus: OrderStatus;
  payStatus: PayStatus;
  payMethod: PayMethod;
  payAccount: string;
  payVoucher: string;
  payTradeNo: string;
  purchaseDate: string;
  placeType: PlaceType;
  orderType: OrderType;
  arrivalStatus: ArrivalStatus;
  arrivalTime: string;
};

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  placed: '已下单',
  completed: '已完成',
  cancelled: '已取消'
};

export const PAY_STATUS_LABEL: Record<PayStatus, string> = {
  pending: '待支付',
  paid: '已支付'
};

export const ARRIVAL_STATUS_LABEL: Record<ArrivalStatus, string> = {
  pending: '未到账',
  received: '已到账'
};

export const ORDER_TYPE_LABEL: Record<OrderType, string> = {
  purchase: '购买',
  upgrade: '升级',
  addon: '加购'
};

export const PLACE_TYPE_LABEL: Record<PlaceType, string> = {
  page: '页面购买',
  admin: '后台代下'
};

export const PAY_METHOD_LABEL: Record<PayMethod, string> = {
  wechat: '微信',
  alipay: '支付宝',
  other: '其他'
};

export const ORDER_TYPE_OPTIONS = [
  { value: 'purchase' as const, label: '购买' },
  { value: 'upgrade' as const, label: '升级' },
  { value: 'addon' as const, label: '加购' }
];

export const PLACE_TYPE_OPTIONS = [
  { value: 'page' as const, label: '页面购买' },
  { value: 'admin' as const, label: '后台代下' }
];

export const PAY_STATUS_OPTIONS = [
  { value: 'pending' as const, label: '待支付' },
  { value: 'paid' as const, label: '已支付' }
];

export const ORDER_STATUS_OPTIONS = [
  { value: 'placed' as const, label: '已下单' },
  { value: 'completed' as const, label: '已完成' },
  { value: 'cancelled' as const, label: '已取消' }
];

export const COUNTRY_OPTIONS = [
  { value: '中国大陆', label: '中国大陆' },
  { value: '越南', label: '越南' },
  { value: '泰国', label: '泰国' },
  { value: '印尼', label: '印尼' },
  { value: '马来西亚', label: '马来西亚' },
  { value: '菲律宾', label: '菲律宾' },
  { value: '新加坡', label: '新加坡' }
];

export function formatMoney(amount: number) {
  return `¥ ${amount.toLocaleString('zh-CN', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })}`;
}

export const adminOrderList: AdminOrder[] = [
  {
    id: 'o-1',
    orderNo: '143009291761225728',
    userName: 'qiu',
    userId: '9681798616',
    phone: '15507737056',
    email: '',
    country: '中国大陆',
    packageName: '专业版',
    packagePrice: 0.01,
    orderAmount: 0.01,
    orderStatus: 'placed',
    payStatus: 'pending',
    payMethod: 'wechat',
    payAccount: '',
    payVoucher: '',
    payTradeNo: '',
    purchaseDate: '',
    placeType: 'page',
    orderType: 'purchase',
    arrivalStatus: 'pending',
    arrivalTime: ''
  },
  {
    id: 'o-2',
    orderNo: '143008812334551041',
    userName: 'lin',
    userId: '9681798601',
    phone: '13800138000',
    email: 'lin@example.com',
    country: '中国大陆',
    packageName: '豪华版',
    packagePrice: 1298,
    orderAmount: 1,
    orderStatus: 'completed',
    payStatus: 'paid',
    payMethod: 'wechat',
    payAccount: 'opCxb17h0kXNDudR1LE_OuAUCSJQ',
    payVoucher: '',
    payTradeNo: '420000250918001122',
    purchaseDate: '2026-09-18',
    placeType: 'page',
    orderType: 'purchase',
    arrivalStatus: 'received',
    arrivalTime: '2026-09-18 10:21:03'
  },
  {
    id: 'o-3',
    orderNo: '143007701122334455',
    userName: 'ming',
    userId: '9681798599',
    phone: '18688886666',
    email: '',
    country: '中国大陆',
    packageName: '专业版',
    packagePrice: 298,
    orderAmount: 298,
    orderStatus: 'cancelled',
    payStatus: 'pending',
    payMethod: 'wechat',
    payAccount: '',
    payVoucher: '',
    payTradeNo: '',
    purchaseDate: '',
    placeType: 'page',
    orderType: 'upgrade',
    arrivalStatus: 'pending',
    arrivalTime: ''
  },
  {
    id: 'o-4',
    orderNo: '143006690011223344',
    userName: 'anna',
    userId: '9681798588',
    phone: '13912345678',
    email: 'anna@wowbit.com',
    country: '越南',
    packageName: '豪华版',
    packagePrice: 1298,
    orderAmount: 1298,
    orderStatus: 'completed',
    payStatus: 'paid',
    payMethod: 'wechat',
    payAccount: 'opCxb17h0kXNDudR1LE_OuAUCSJQ',
    payVoucher: '',
    payTradeNo: '420000250917009988',
    purchaseDate: '2026-09-17',
    placeType: 'page',
    orderType: 'purchase',
    arrivalStatus: 'received',
    arrivalTime: '2026-09-17 15:08:44'
  },
  {
    id: 'o-5',
    orderNo: '143005580099887766',
    userName: 'kai',
    userId: '9681798577',
    phone: '13700001111',
    email: '',
    country: '中国大陆',
    packageName: '专业版',
    packagePrice: 298,
    orderAmount: 1,
    orderStatus: 'placed',
    payStatus: 'pending',
    payMethod: 'wechat',
    payAccount: '',
    payVoucher: '',
    payTradeNo: '',
    purchaseDate: '',
    placeType: 'page',
    orderType: 'addon',
    arrivalStatus: 'pending',
    arrivalTime: ''
  },
  {
    id: 'o-6',
    orderNo: '143004470088776655',
    userName: 'zoe',
    userId: '9681798566',
    phone: '15677778888',
    email: 'zoe@test.com',
    country: '泰国',
    packageName: '豪华版',
    packagePrice: 1298,
    orderAmount: 1298,
    orderStatus: 'completed',
    payStatus: 'paid',
    payMethod: 'alipay',
    payAccount: '2088123456789012',
    payVoucher: '',
    payTradeNo: '20260916220014',
    purchaseDate: '2026-09-16',
    placeType: 'page',
    orderType: 'purchase',
    arrivalStatus: 'pending',
    arrivalTime: ''
  },
  {
    id: 'o-7',
    orderNo: '143003360077665544',
    userName: 'jay',
    userId: '9681798555',
    phone: '15507737056',
    email: '',
    country: '中国大陆',
    packageName: '专业版',
    packagePrice: 298,
    orderAmount: 298,
    orderStatus: 'cancelled',
    payStatus: 'pending',
    payMethod: 'wechat',
    payAccount: '',
    payVoucher: '',
    payTradeNo: '',
    purchaseDate: '',
    placeType: 'page',
    orderType: 'purchase',
    arrivalStatus: 'pending',
    arrivalTime: ''
  },
  {
    id: 'o-8',
    orderNo: '143002250066554433',
    userName: 'qiu',
    userId: '9681798616',
    phone: '15507737056',
    email: '',
    country: '中国大陆',
    packageName: '专业版',
    packagePrice: 0.01,
    orderAmount: 0.01,
    orderStatus: 'completed',
    payStatus: 'paid',
    payMethod: 'wechat',
    payAccount: 'opCxb17h0kXNDudR1LE_OuAUCSJQ',
    payVoucher: '',
    payTradeNo: '420000250915003344',
    purchaseDate: '2026-09-15',
    placeType: 'page',
    orderType: 'purchase',
    arrivalStatus: 'received',
    arrivalTime: '2026-09-15 09:40:12'
  }
];
